import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import client from "../../../api/camasirhaneApi";
import { useAuth } from "../context/CamasirhaneRoleContext";
import { usePeriodStatus } from "../hooks/usePeriodStatus";
import { canWrite, isAdmin } from "../utils/auth";
import {
  MONTHS,
  YEARS,
  WEEKDAYS_SHORT,
  daysInMonth,
  formatMaterialLabel,
  formatNum,
  getWeighingAvgCellHighlight,
} from "../utils/constants";
import { cellNumericValue, commitCellExpression, parseCellInput } from "../utils/cellExpression";
import AvgHighlightFilter from "./AvgHighlightFilter";
import "../styles/shared.css";

export default function WeighingEntryScreen() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const admin = isAdmin(user);

  const [hotels, setHotels] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [hotelId, setHotelId] = useState(null);
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(7);
  const [weighingType, setWeighingType] = useState("DIRTY");
  const [otherTypeValues, setOtherTypeValues] = useState({});
  const [values, setValues] = useState({});
  const [originalValues, setOriginalValues] = useState({});
  const [deptMaterialMap, setDeptMaterialMap] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [avgHighlightFilters, setAvgHighlightFilters] = useState(["below"]);

  const { isClosed } = usePeriodStatus(year, month);
  const readOnly = !writable || (isClosed && !admin);

  const days = useMemo(() => Array.from({ length: daysInMonth(year, month) }, (_, i) => i + 1), [year, month]);
  const otherType = weighingType === "DIRTY" ? "CLEAN" : "DIRTY";

  const dayMeta = useMemo(() => {
    const now = new Date();
    const isCurrentMonth = now.getFullYear() === year && now.getMonth() + 1 === month;
    const todayDate = now.getDate();
    return days.map((d) => {
      const dow = new Date(year, month - 1, d).getDay();
      return {
        day: d,
        label: WEEKDAYS_SHORT[dow],
        isWeekend: dow === 0 || dow === 6,
        isToday: isCurrentMonth && d === todayDate,
      };
    });
  }, [days, year, month]);

  useEffect(() => {
    Promise.all([
      client.get("/hotels/"),
      client.get("/departments/"),
      client.get("/materials/"),
    ]).then(([h, d, m]) => {
      setHotels(h.data);
      setDepartments(d.data);
      setMaterials(m.data);
      if (h.data.length) setHotelId(h.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!hotelId) { setDeptMaterialMap(null); return; }
    client.get("/weighing-records/department-materials/", { params: { hotel: hotelId } })
      .then((res) => {
        const map = new Map();
        (res.data || []).forEach((p) => {
          if (!map.has(p.department_id)) map.set(p.department_id, new Set());
          map.get(p.department_id).add(p.material_id);
        });
        setDeptMaterialMap(map);
      })
      .catch(() => setDeptMaterialMap(null));
  }, [hotelId]);

  useEffect(() => {
    if (!hotelId) return;
    setValues({});
    setOriginalValues({});
    client.get("/weighing-records/", { params: { hotel: hotelId, year, month, weighing_type: weighingType } })
      .then((res) => {
        const next = {};
        res.data.forEach((rec) => { next[cellKey(rec.department_id, rec.material_id, rec.day)] = Number(rec.weight_kg); });
        setValues(next);
        setOriginalValues(next);
      }).catch(() => {});
    client.get("/weighing-records/", { params: { hotel: hotelId, year, month, weighing_type: otherType } })
      .then((res) => {
        const next = {};
        res.data.forEach((rec) => { next[cellKey(rec.department_id, rec.material_id, rec.day)] = Number(rec.weight_kg); });
        setOtherTypeValues(next);
      }).catch(() => setOtherTypeValues({}));
  }, [hotelId, year, month, weighingType, otherType]);

  function cellKey(d, m, day) { return `${d}|${m}|${day}`; }

  function handleCellChange(d, m, day, raw) {
    const key = cellKey(d, m, day);
    setValues((prev) => ({ ...prev, [key]: parseCellInput(raw) }));
  }

  function handleCellBlur(d, m, day) {
    const key = cellKey(d, m, day);
    setValues((prev) => {
      const current = prev[key];
      if (current === "" || current == null || typeof current === "number") return prev;
      return { ...prev, [key]: commitCellExpression(current) };
    });
  }

  function rowTotal(d, m) {
    return days.reduce((s, day) => s + cellNumericValue(values[cellKey(d, m, day)]), 0);
  }

  function rowMonthlyAverage(d, m) {
    const entries = days
      .map((day) => {
        const raw = values[cellKey(d, m, day)];
        if (raw === "" || raw == null) return null;
        return cellNumericValue(raw);
      })
      .filter((value) => value != null);

    if (!entries.length) return null;
    return entries.reduce((sum, value) => sum + value, 0) / entries.length;
  }

  function getAvgHighlightClass(d, m, day, average) {
    if (average == null) return "";
    const raw = values[cellKey(d, m, day)];
    if (raw === "" || raw == null) return "";
    const highlight = getWeighingAvgCellHighlight(cellNumericValue(raw), average, avgHighlightFilters);
    if (highlight === "below") return " below-avg-cell";
    if (highlight === "above") return " above-avg-cell";
    return "";
  }

  const rowAverageMap = useMemo(() => {
    const map = new Map();
    departments.forEach((dept) => {
      materials.forEach((mat) => {
        const avg = rowMonthlyAverage(dept.id, mat.id);
        if (avg != null) map.set(`${dept.id}|${mat.id}`, avg);
      });
    });
    return map;
  }, [departments, materials, values, days]);

  function deptTotal(deptId) {
    return materials.reduce((s, m) => s + rowTotal(deptId, m.id), 0);
  }

  function colTotal(day) {
    let sum = 0;
    departments.forEach((d) => materials.forEach((m) => {
      sum += cellNumericValue(values[cellKey(d.id, m.id, day)]);
    }));
    return sum;
  }

  const grandTotal = days.reduce((s, day) => s + colTotal(day), 0);
  const otherGrandTotal = useMemo(() => Object.values(otherTypeValues).reduce((s, v) => s + (typeof v === "number" ? v : 0), 0), [otherTypeValues]);

  const bodyXRef = useRef(null);
  const headXRef = useRef(null);
  const barScrollRef = useRef(null);
  const hBarWrapRef = useRef(null);
  const gridWrapperRef = useRef(null);
  const layoutSyncFrameRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [showHBar, setShowHBar] = useState(false);

  const runTableLayoutSync = () => {
    const bodyX = bodyXRef.current;
    const headX = headXRef.current;
    const wrapper = gridWrapperRef.current;
    if (!bodyX) return;

    const bodyTable = bodyX.querySelector("table.data-grid--weighing");
    const headTable = headX?.querySelector("table.data-grid--weighing");
    const tables = [headTable, bodyTable].filter(Boolean);
    if (!bodyTable) return;

    const deptCell = bodyTable.querySelector("td.dept-cell");
    const matCells = bodyTable.querySelectorAll("td.mat-cell");
    const deptW = deptCell?.offsetWidth || 150;
    let matW = 200;
    matCells.forEach((cell) => {
      matW = Math.max(matW, cell.scrollWidth);
    });

    if (wrapper) {
      wrapper.style.setProperty("--weighing-dept-w", `${deptW}px`);
      wrapper.style.setProperty("--weighing-mat-w", `${matW}px`);
    }

    tables.forEach((table) => {
      const cols = table.querySelectorAll("col");
      if (cols[0]) cols[0].style.width = `${deptW}px`;
      if (cols[1]) cols[1].style.width = `${matW}px`;
      table.querySelectorAll("th.dept-col, td.dept-cell").forEach((el) => {
        el.style.width = `${deptW}px`;
        el.style.minWidth = `${deptW}px`;
      });
      table.querySelectorAll("th.mat-col, td.mat-cell").forEach((el) => {
        el.style.width = `${matW}px`;
        el.style.minWidth = `${matW}px`;
      });
      const w = table.scrollWidth;
      table.style.width = `${w}px`;
      table.style.minWidth = `${w}px`;
    });

    const w = bodyTable.scrollWidth;
    setScrollWidth((prev) => (prev === w ? prev : w));
    const nextShowHBar = w > bodyX.clientWidth + 1;
    setShowHBar((prev) => (prev === nextShowHBar ? prev : nextShowHBar));
  };

  const scheduleTableLayoutSync = () => {
    if (layoutSyncFrameRef.current != null) return;
    layoutSyncFrameRef.current = requestAnimationFrame(() => {
      layoutSyncFrameRef.current = null;
      runTableLayoutSync();
    });
  };

  useEffect(() => {
    const bodyX = bodyXRef.current;
    const headX = headXRef.current;
    if (!bodyX) return;

    scheduleTableLayoutSync();
    const ro = new ResizeObserver(() => scheduleTableLayoutSync());
    ro.observe(bodyX);
    if (headX) ro.observe(headX);
    window.addEventListener("resize", scheduleTableLayoutSync);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", scheduleTableLayoutSync);
      if (layoutSyncFrameRef.current != null) {
        cancelAnimationFrame(layoutSyncFrameRef.current);
        layoutSyncFrameRef.current = null;
      }
    };
  }, [dayMeta.length, departments.length, materials.length]);

  useEffect(() => {
    scheduleTableLayoutSync();
  }, [values, grandTotal]);

  useEffect(() => {
    const bodyX = bodyXRef.current;
    const headX = headXRef.current;
    const bar = barScrollRef.current;
    if (!bodyX) return;

    let syncing = false;
    const sync = (left, source) => {
      if (syncing) return;
      syncing = true;
      if (source !== "body") bodyX.scrollLeft = left;
      if (headX && source !== "head") headX.scrollLeft = left;
      if (bar && source !== "bar") bar.scrollLeft = left;
      syncing = false;
    };

    const onBody = () => sync(bodyX.scrollLeft, "body");
    const onHead = () => { if (headX) sync(headX.scrollLeft, "head"); };
    const onBar = () => { if (bar) sync(bar.scrollLeft, "bar"); };

    bodyX.addEventListener("scroll", onBody, { passive: true });
    headX?.addEventListener("scroll", onHead, { passive: true });
    bar?.addEventListener("scroll", onBar, { passive: true });
    sync(bodyX.scrollLeft, "body");
    return () => {
      bodyX.removeEventListener("scroll", onBody);
      headX?.removeEventListener("scroll", onHead);
      bar?.removeEventListener("scroll", onBar);
    };
  }, [showHBar, scrollWidth, dayMeta.length, departments.length, materials.length]);

  useEffect(() => {
    if (!showHBar) return;
    const bar = hBarWrapRef.current;
    if (!bar) return;

    const syncBarBounds = () => {
      const main = bar.closest("main");
      if (!main) return;
      const rect = main.getBoundingClientRect();
      bar.style.left = `${rect.left}px`;
      bar.style.width = `${rect.width}px`;
    };

    syncBarBounds();
    window.addEventListener("resize", syncBarBounds);
    const ro = new ResizeObserver(syncBarBounds);
    const main = bar.closest("main");
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener("resize", syncBarBounds);
      ro.disconnect();
    };
  }, [showHBar]);

  const tableCols = (
    <colgroup>
      <col className="col-dept" />
      <col className="col-mat" />
      {dayMeta.map((dm) => <col key={dm.day} className="col-day" />)}
      <col className="col-total" />
    </colgroup>
  );

  const tableHeadRow = (
    <tr>
      <th className="label-col dept-col">Departman</th>
      <th className="label-col mat-col">Malzeme</th>
      {dayMeta.map((dm) => (
        <th
          key={dm.day}
          className={`day-col${dm.isWeekend ? " weekend" : ""}${dm.isToday ? " today" : ""}`}
          title={dm.isToday ? "Bugün" : undefined}
        >
          <span className="day-weekday">{dm.label}</span>
          <span className="day-number">{dm.day}</span>
        </th>
      ))}
      <th className="total-col">Toplam</th>
    </tr>
  );

  async function handleSave() {
    setSaving(true);
    setSaveMessage("");
    const records = [];
    const seenKeys = new Set();

    Object.entries(values).forEach(([key, weight_kg]) => {
      seenKeys.add(key);
      const [department_id, material_id, day] = key.split("|").map(Number);
      const base = { hotel_id: hotelId, year, month, day, department_id, material_id, weighing_type: weighingType };
      const resolved = typeof weight_kg === "string" ? commitCellExpression(weight_kg) : weight_kg;
      if (resolved === "" || resolved === undefined) {
        if (originalValues[key] !== undefined && originalValues[key] !== "") {
          records.push({ ...base, delete: true });
        }
        return;
      }
      records.push({ ...base, weight_kg: resolved });
    });

    Object.keys(originalValues).forEach((key) => {
      if (seenKeys.has(key)) return;
      const [department_id, material_id, day] = key.split("|").map(Number);
      records.push({
        hotel_id: hotelId,
        year,
        month,
        day,
        department_id,
        material_id,
        weighing_type: weighingType,
        delete: true,
      });
    });

    if (!records.length) {
      setSaveMessage("Kaydedilecek değişiklik yok.");
      setSaving(false);
      return;
    }

    try {
      await client.post("/weighing-records/bulk/", { records });
      const res = await client.get("/weighing-records/", { params: { hotel: hotelId, year, month, weighing_type: weighingType } });
      const next = {};
      res.data.forEach((rec) => { next[cellKey(rec.department_id, rec.material_id, rec.day)] = Number(rec.weight_kg); });
      setValues(next);
      setOriginalValues(next);
      setSaveMessage(`Kaydedildi (${user.first_name || user.username}).`);
    } catch (err) {
      setSaveMessage(err.response?.data?.detail || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={`screen weighing-screen${showHBar ? " weighing-screen--hbar" : ""}`}>
      <div className="weighing-scroll">
      <div className="screen-header">
        <h2>Kirli / Temiz Tartım</h2>
        <p>Günlük tartım kayıtları — kilogram bazında veri girişi</p>
      </div>

      {isClosed && (
        <div className="locked-banner">🔒 Bu dönem kapalı. {admin ? "Yönetici olarak düzenleyebilirsiniz." : "Veri girişi yapılamaz."}</div>
      )}

      <div className="toolbar">
        <select className="ui-select" value={hotelId ?? ""} onChange={(e) => setHotelId(Number(e.target.value))}>
          {hotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        <select className="ui-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
        </select>
        <select className="ui-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="type-toggle">
          <button className={weighingType === "DIRTY" ? "active" : ""} onClick={() => setWeighingType("DIRTY")}>Kirli</button>
          <button className={weighingType === "CLEAN" ? "active" : ""} onClick={() => setWeighingType("CLEAN")}>Temiz</button>
        </div>
        <AvgHighlightFilter value={avgHighlightFilters} onChange={setAvgHighlightFilters} />
        {!readOnly && (
          <button className="ui-btn" onClick={handleSave} disabled={saving || !hotelId}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        )}
        {saveMessage && <span className={`status-msg ${saveMessage.includes("Kaydedildi") ? "success" : "error"}`}>{saveMessage}</span>}
      </div>

      <div className="summary-cards">
        <div className="summary-card"><h3>{weighingType === "DIRTY" ? "Kirli" : "Temiz"} Toplam</h3><p>{formatNum(grandTotal, 1)} kg</p></div>
        <div className="summary-card"><h3>{otherType === "DIRTY" ? "Kirli" : "Temiz"} Toplam</h3><p>{formatNum(otherGrandTotal, 1)} kg</p></div>
        <div className="summary-card"><h3>Fark</h3><p>{formatNum(Math.abs(grandTotal - otherGrandTotal), 1)} kg</p></div>
      </div>

      <div className="grid-wrapper grid-wrapper--weighing" ref={gridWrapperRef}>
        <div className="weighing-thead-bar">
          <div className="weighing-grid-x weighing-grid-x--head" ref={headXRef}>
            <table className="data-grid data-grid--weighing">
              {tableCols}
              <thead>{tableHeadRow}</thead>
            </table>
          </div>
        </div>
        <div className="weighing-grid-x weighing-grid-x--body" ref={bodyXRef}>
          <table className="data-grid data-grid--weighing">
            {tableCols}
            <tbody>
              {departments.map((dept) => {
                const hasMap = deptMaterialMap && deptMaterialMap.size > 0;
                let deptMaterials = materials.filter((m) => m.is_active !== false);
                if (hasMap) {
                  const set = deptMaterialMap.get(dept.id);
                  if (!set || set.size === 0) return null;
                  deptMaterials = deptMaterials
                    .filter((m) => set.has(m.id))
                    .sort((a, b) => a.id - b.id);
                }
                if (!deptMaterials.length) return null;
                return (
                  <Fragment key={dept.id}>
                    {deptMaterials.map((mat, idx) => {
                      const rowAvg = rowAverageMap.get(`${dept.id}|${mat.id}`);
                      return (
                      <tr key={`${dept.id}-${mat.id}`} className="data-row">
                        {idx === 0 && (
                          <td className="sticky-col label-col dept-cell" rowSpan={deptMaterials.length}>
                            <span className="dept-name">{dept.name}</span>
                            <span className="dept-sum">{formatNum(deptTotal(dept.id), 1)} kg</span>
                          </td>
                        )}
                        <td className="label-col mat-cell">{formatMaterialLabel(mat.name)}</td>
                        {dayMeta.map((dm) => {
                          const avgHighlightClass = getAvgHighlightClass(dept.id, mat.id, dm.day, rowAvg);
                          return (
                          <td
                            key={dm.day}
                            className={`day-cell${dm.isWeekend ? " weekend-cell" : ""}${dm.isToday ? " today-cell" : ""}${avgHighlightClass}`}
                          >
                            <input type="text" inputMode="decimal" disabled={readOnly}
                              value={values[cellKey(dept.id, mat.id, dm.day)] ?? ""}
                              onChange={(e) => handleCellChange(dept.id, mat.id, dm.day, e.target.value)}
                              onBlur={() => handleCellBlur(dept.id, mat.id, dm.day)} />
                          </td>
                          );
                        })}
                        <td className="total-col">{formatNum(rowTotal(dept.id, mat.id), 1)}</td>
                      </tr>
                      );
                    })}
                    <tr className="dept-divider"><td colSpan={3 + dayMeta.length} /></tr>
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="grand-row">
                <td className="sticky-col label-col grand-label" colSpan={2}>Günlük Toplam</td>
                {dayMeta.map((dm) => {
                  const t = colTotal(dm.day);
                  return (
                    <td key={dm.day} className={`day-cell${dm.isWeekend ? " weekend-cell" : ""}${dm.isToday ? " today-cell" : ""}`}>
                      {t ? formatNum(t, 1) : ""}
                    </td>
                  );
                })}
                <td className="total-col grand-total">{formatNum(grandTotal, 1)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      </div>

      {showHBar && (
        <div className="weighing-hscroll-bar" ref={hBarWrapRef} aria-hidden="true">
          <div className="weighing-hscroll-track" ref={barScrollRef}>
            <div className="weighing-hscroll-inner" style={{ width: scrollWidth }} />
          </div>
        </div>
      )}
    </div>
  );
}
